import PageDrawerMobile, { PageDrawerMobileRef } from "@/components/common/PageDrawerMobile"
import Calendars, { CalendarsProps, CalendarsRef, SelectedDates } from "@/components/common/TEDatePicker/Calendars"
import Text from "@/components/common/Typography/Text"
import { useAppSelector } from "@/lib/redux/hooks"

import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Col, Row } from "antd"
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

type DrawerSelectDateProps = CalendarsProps & {

}

export type DrawerSelectDateRef = Omit<PageDrawerMobileRef, 'open'> & {
  open: (selector?: 'to' | 'from' | undefined) => void
}

const DrawerSelectDate = forwardRef<DrawerSelectDateRef, DrawerSelectDateProps>((props, ref) => {
  const refPageDrawerMobile = useRef<PageDrawerMobileRef>(null)
  const [numberOfMonths, setNumberOfMonths] = useState(2)
  const [value, setValue] = useState<SelectedDates>([null, null])
  const [selector, setSelector] = useState<'to' | 'from' | undefined>()
  const refCalendars = useRef<CalendarsRef>(null)
  const { t } = useTranslation()

  useImperativeHandle(ref, () => {
    return {
      open: (selector) => {
        refPageDrawerMobile.current?.open()
        setSelector(selector)
        setValue([null, null])
      },
      close: () => {
        setValue([null, null])
        refPageDrawerMobile.current?.close()
      },
    }
  }, [])

  const onWindowResize = () => {
    const width = document.documentElement.clientWidth
    if (width > 720) {
      setNumberOfMonths(4)
    } else {
      setNumberOfMonths(2)
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      onWindowResize()
    }
    window.addEventListener('resize', onWindowResize)
    return () => {
      window.removeEventListener('resize', onWindowResize)
    }
  }, [])

  return (
    <PageDrawerMobile
      ref={refPageDrawerMobile}
      headerProps={{
        shownRight: false,
        title: (
          <Row justify='center'>
            <Col>
              <Text strong>{t("common.date_label")}</Text>
            </Col>
          </Row>
        )
      }}
      footer={(
        <>
          <Col span={24} style={{ marginBottom: '1rem' }}>
            <Row justify='space-between' align='middle'>
              <Col>
                <Button onClick={() => refCalendars.current?.prev()} icon={<FontAwesomeIcon icon={faChevronLeft} />} type='text' size='large'>
                  {t("common.prev_months")}
                </Button>
              </Col>
              <Col>
                <Button onClick={() => refCalendars.current?.next()} iconPlacement='end' icon={<FontAwesomeIcon icon={faChevronRight} />} type='text' size='large'>
                  {t("common.next_months")}
                </Button>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Button
              onClick={() => {
                const isNotSelected = !value || value.every(v => !v)
                !isNotSelected && props.onChange && props.onChange(value)
                refPageDrawerMobile.current?.close()
              }}
              size='large'
              type='primary'
              block
            >
              {t("common.confirm")}
            </Button>
          </Col>
        </>
      )}
      styles={{
        wrapper: {
          top: 0
        },
        mask: {
          top: 0
        }
      }}
    >
      <Calendars
        {...props}
        onChange={(val) => {
          setValue(val)

          // In range mode, only close and notify parent when both dates are selected
          const rangeMode = props.range || selector === 'to'
          const isSelectionComplete = !rangeMode || (val[0] && val[1])

          if (isSelectionComplete) {
            props.onChange && props.onChange(val)
            setValue([null, null])
            refPageDrawerMobile.current?.close()
          }
        }}
        range={props.range || selector === 'to'}
        selector={selector}
        ref={refCalendars}
        numberOfMonths={numberOfMonths}
        type='drawer'
      />
    </PageDrawerMobile>
  )
})

DrawerSelectDate.displayName = 'DrawerSelectDate'

export default DrawerSelectDate